import React, { useEffect, useLayoutEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { LetterNotificationProvider } from './contexts/LetterNotificationContext';
import { CursorProvider } from './contexts/CursorContext';
import FullscreenHandler from './components/FullscreenHandler';
import AdminGearIcon from './components/AdminGearIcon';
import BirdNotification from './components/BirdNotification';
import MailNotification from './components/MailNotification';
import ActivePulse from './components/ActivePulse';
import CustomCursorWrapper from './components/CustomCursorWrapper';
import MobilePrompt from './components/MobilePrompt';
import useIsMobile from './hooks/useIsMobile';
import Login from './pages/Login';
import Home from './pages/Home';
import Letters from './pages/Letters';
import Poetry from './pages/Poetry';
import Journal from './pages/Journal';
import Puzzle from './pages/Puzzle';
import FeetWatercolor from './pages/FeetWatercolor';
import Intimacy from './pages/Intimacy';
import FoggedMirror from './pages/FoggedMirror';
import ErosPoetry from './pages/ErosPoetry';
import VoiceMemos from './pages/VoiceMemos';
import ComingSoon from './pages/ComingSoon';
import Admin from './pages/Admin';
import DateIdeas from './pages/DateIdeas';
import './styles/App.css';

// Component to reset scroll position on route changes
const ScrollToTop = () => {
  const { pathname } = useLocation();

  // Use useLayoutEffect to scroll BEFORE browser paints
  useLayoutEffect(() => {
    // Immediately scroll to top, synchronously
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname]);

  return null;
};

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { currentUser, userProfile, loading } = useAuth();

  // Show loading screen while checking authentication
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  // If not loading and no user, redirect to login
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // Check if user is admin
  const isAdmin = userProfile?.name?.toLowerCase() === 'admin';

  // If admin user trying to access non-admin pages, redirect to admin panel
  if (isAdmin && !adminOnly) {
    return <Navigate to="/admin" replace />;
  }

  // If non-admin user trying to access admin-only page, redirect to home
  if (!isAdmin && adminOnly) {
    return <Navigate to="/home" replace />;
  }

  // User is authenticated and authorized, render children
  return children;
};

// Login Route Component (redirect if already logged in)
const LoginRoute = () => {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  // If already logged in, redirect to home
  if (currentUser) {
    return <Navigate to="/home" replace />;
  }

  return <Login />;
};

// Main App Component
function App() {
  const isMobile = useIsMobile();

  // If mobile device detected, show mobile prompt instead of app
  if (isMobile) {
    return <MobilePrompt />;
  }

  // Desktop experience - render normally
  return (
    <AuthProvider>
      <Router>
        <NotificationProvider>
          <LetterNotificationProvider>
            <CursorProvider>
          <ScrollToTop />
          <FullscreenHandler>
            <AdminGearIcon />
            <BirdNotification />
            <MailNotification />
            <ActivePulse />
            <CustomCursorWrapper />
            <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route
              path="/home"
              element={
                <ProtectedRoute>
                  <Home />
                </ProtectedRoute>
              }
            />
            <Route
              path="/letters"
              element={
                <ProtectedRoute>
                  <Letters />
                </ProtectedRoute>
              }
            />
            <Route
              path="/poetry"
              element={
                <ProtectedRoute>
                  <Poetry />
                </ProtectedRoute>
              }
            />
            <Route
              path="/journal"
              element={
                <ProtectedRoute>
                  <Journal />
                </ProtectedRoute>
              }
            />
            <Route
              path="/puzzle"
              element={
                <ProtectedRoute>
                  <Puzzle />
                </ProtectedRoute>
              }
            />
            <Route
              path="/feet-watercolor"
              element={
                <ProtectedRoute>
                  <FeetWatercolor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/intimacy"
              element={
                <ProtectedRoute>
                  <Intimacy />
                </ProtectedRoute>
              }
            />
            <Route
              path="/fogged-mirror"
              element={
                <ProtectedRoute>
                  <FoggedMirror />
                </ProtectedRoute>
              }
            />
            <Route
              path="/eros-poetry"
              element={
                <ProtectedRoute>
                  <ErosPoetry />
                </ProtectedRoute>
              }
            />
            <Route
              path="/voice-memos"
              element={
                <ProtectedRoute>
                  <VoiceMemos />
                </ProtectedRoute>
              }
            />
            <Route
              path="/coming-soon"
              element={
                <ProtectedRoute>
                  <ComingSoon />
                </ProtectedRoute>
              }
            />
            <Route
              path="/date-ideas"
              element={
                <ProtectedRoute>
                  <DateIdeas />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute adminOnly={true}>
                  <Admin />
                </ProtectedRoute>
              }
            />
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </FullscreenHandler>
            </CursorProvider>
          </LetterNotificationProvider>
        </NotificationProvider>
      </Router>
    </AuthProvider>
  );
}

export default App;